import type { SelectedFile } from "./SelectedFile";

import React from "react";

const UPLOAD_TIMEOUT_MS = 15_000;

/**
 * XHR library can load files from a URI directly, if we pass it an object like this
 */
type XHRURIObject = {
  uri: string;
  type: string;
  name: string;
};

/**
 * Upload a file to S3 using XMLHttpRequest with progress tracking.
 * Accepts either a web Blob/File or a React Native file object with URI.
 * XHR send takes a object of form { uri ... }. We cannot use fetch in this repo
 * And we also cannot use FileSystem.uploadAsync in this version of expo as it was deprecated.
 * We also cannot use the expofile as a blob for XHR as that does not work on mobile react-native
 */
function uploadToS3WithXHR(
  uploadUrl: string,
  body: Blob | XHRURIObject,
  contentType: string,
  onProgress: (percentComplete: number) => void,
  onLoadingChange: (loading: boolean) => void,
  /** When RN XHR omits total bytes (lengthComputable false), use file size for progress. */
  contentLengthBytes?: number,
): { promise: Promise<Response>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<Response>((resolve, reject) => {
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.min(100, (event.loaded / event.total) * 100));
      } else if (contentLengthBytes !== undefined && contentLengthBytes > 0) {
        onProgress(Math.min(100, (event.loaded / contentLengthBytes) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // RN often omits upload progress events on subsequent PUTs; ensure we still end at 100%.
        onProgress(100);
      }
      onLoadingChange(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        resolve(xhr.response as Response);
      } else {
        reject(new Error(xhr.statusText || "Upload failed"));
      }
    });

    xhr.addEventListener("error", () => {
      onLoadingChange(false);
      console.error("[useS3Upload] Network error during upload");
      reject(new Error("Network error"));
    });

    xhr.addEventListener("abort", () => {
      onLoadingChange(false);
      reject(new Error("Upload aborted"));
    });

    xhr.addEventListener("timeout", () => {
      onLoadingChange(false);
      console.error("[useS3Upload] Upload timed out");
      reject(new Error("Upload timed out"));
    });

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.send(body);
  });

  return { promise, abort: () => xhr.abort() };
}

export function useS3Upload() {
  const [loading, setLoading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const abortRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    return () => {
      abortRef.current?.();
    };
  }, []);

  const resetProgress = React.useCallback(() => {
    setProgress(0);
  }, []);

  const uploadFileToS3 = React.useCallback(
    async (uploadUrl: string, selectedFile: SelectedFile) => {
      abortRef.current?.();
      setLoading(true);
      setProgress(0);

      try {
        let body: Blob | XHRURIObject;

        if (typeof selectedFile.file === "string") {
          body = {
            uri: selectedFile.file,
            type: selectedFile.mimeType,
            name: selectedFile.name,
          };
        } else {
          // Web: Use the File/Blob directly
          body = selectedFile.file;
        }

        const { promise, abort } = uploadToS3WithXHR(
          uploadUrl,
          body,
          selectedFile.mimeType,
          setProgress,
          setLoading,
          selectedFile.size,
        );
        abortRef.current = abort;
        return await promise;
      } catch (err) {
        abortRef.current = null;
        setLoading(false);
        console.error("[useS3Upload] Upload error:", err);
        throw err;
      }
    },
    [],
  );

  const resetUploadState = React.useCallback(() => {
    abortRef.current?.();
    abortRef.current = null;
    setLoading(false);
    setProgress(0);
  }, []);

  return { uploadFileToS3, loading, progress, resetProgress, resetUploadState };
}
